'use client';

import { Transition } from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { cn } from 'utils/cn';
import { SearchButtonSVG } from '../cta-navbar/search-button-svg';

export default function Search() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchId = 'search-input';
  const searchDialogId = 'search-dialog';

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }

    // Close search on escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent scrolling when search is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const searchValue = inputRef.current?.value;
    if (searchValue) {
      router.push(`/search?q=${encodeURIComponent(searchValue)}`);
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
    }
  };

  return (
    <>
      <button
        className={cn('rounded-full px-4 py-2 transition-colors hover:bg-neutral-100', {
          'bg-neutral-100': isOpen
        })}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-controls={searchDialogId}
        aria-label="Open search"
        aria-haspopup="dialog"
      >
        <SearchButtonSVG />
      </button>

      <Transition
        show={isOpen}
        as="div"
        enter="transition-opacity duration-200"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div 
          className="fixed inset-0 z-50 bg-black/20" 
          onClick={() => setIsOpen(false)}
          role="presentation"
          aria-hidden="true"
          tabIndex={-1}
        />
      </Transition>

      <Transition
        show={isOpen}
        as="div"
        enter="transition-all duration-200"
        enterFrom="opacity-0 translate-y-4"
        enterTo="opacity-100 translate-y-0"
        leave="transition-all duration-200"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-4"
      >
        <div 
          id={searchDialogId}
          className="fixed left-0 right-0 top-[calc(var(--navbar-height)_+_var(--marquee-banner-height))] z-50 mx-auto mt-4 w-full max-w-2xl px-4"
          role="dialog"
          aria-modal="true"
          aria-label="Search products"
          aria-describedby="search-description"
        >
          <div id="search-description" className="sr-only">
            Search for products by entering keywords in the search field below.
          </div>
          <form onSubmit={handleSubmit} className="w-full">
            <label htmlFor={searchId} className="sr-only">
              Search for products
            </label>
            <div className="relative">
              <input
                ref={inputRef}
                id={searchId}
                key={searchParams?.get('q')}
                type="text"
                name="q"
                placeholder="Search for products..."
                autoComplete="off"
                defaultValue={searchParams?.get('q') || ''}
                className="w-full rounded-lg border bg-white px-4 py-3 text-black shadow-xl placeholder:text-neutral-500 focus:border-none focus:outline-none focus:ring-2 focus:ring-black dark:border-neutral-800 dark:bg-neutral-900 dark:text-white dark:placeholder:text-neutral-400"
                aria-label="Search for products"
                onKeyDown={handleKeyDown}
                aria-required="true"
                aria-invalid="false"
              />
              <button
                type="submit"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-black"
                aria-label="Submit search"
              >
                <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </form>
        </div>
      </Transition>
    </>
  );
}

export function SearchSkeleton() {
  return (
    <form className="w-max-[550px] relative w-full lg:w-80 xl:w-full">
      <label htmlFor="search-skeleton" className="sr-only">
        Search for products
      </label>
      <div className="relative">
        <input
          id="search-skeleton"
          placeholder="Search for products..."
          className="w-full rounded-lg border bg-white px-4 py-2 text-sm text-black placeholder:text-neutral-500 focus:border-none focus:outline-none focus:ring-0 dark:border-neutral-800 dark:bg-transparent dark:text-white dark:placeholder:text-neutral-400"
          aria-label="Search for products"
          disabled
          aria-disabled="true"
        />
        <div className="absolute right-0 top-0 mr-3 flex h-full items-center" aria-hidden="true">
          <MagnifyingGlassIcon className="h-4" />
        </div>
      </div>
    </form>
  );
}
